// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import type {FeedTopicResponse} from '@fluxer/schema/src/domains/feed/FeedTopicSchemas';
import {makeAutoObservable} from 'mobx';

// Topics live a day or so and their post counts move, so a feed page opened later than this asks again.
export const FEED_TOPICS_STALE_MS = 60 * 1000;

// Topics opened from a post's tag are kept for the most recent ones only; the oldest goes first.
const OPENED_TOPICS_MAX = 64;

// Micup akis-gundem: the live topics of the caller's country (at most 20, newest first) and the few topics opened from
// a post's tag that the live list no longer holds (ended ones). Plain state; FeedTopicCommands asks the server.
class FeedTopics {
	topics: ReadonlyArray<FeedTopicResponse> = [];
	// A Map keeps insertion order for numeric ids too, so the first key is the least recently opened.
	opened = new Map<string, FeedTopicResponse>();
	fetchedAt: number | null = null;
	isLoading = false;
	failure: 'forbidden' | 'error' | null = null;
	// Bumped on reset, so an answer for the previous account is dropped.
	private session = 0;

	constructor() {
		makeAutoObservable<FeedTopics, 'session'>(this, {session: false}, {autoBind: true});
	}

	reset(): void {
		this.session++;
		this.topics = [];
		this.opened.clear();
		this.fetchedAt = null;
		this.isLoading = false;
		this.failure = null;
	}

	getSession(): number {
		return this.session;
	}

	// The session a request belongs to, or null when none should start (one in flight, or the list is fresh).
	beginLoad(force: boolean): number | null {
		if (this.isLoading) return null;
		if (!force && this.fetchedAt !== null && Date.now() - this.fetchedAt <= FEED_TOPICS_STALE_MS) return null;
		this.isLoading = true;
		return this.session;
	}

	handleLoaded(session: number, list: ReadonlyArray<FeedTopicResponse>): void {
		if (session !== this.session) return;
		this.topics = list;
		this.fetchedAt = Date.now();
		this.isLoading = false;
		this.failure = null;
	}

	handleLoadFailed(session: number, failure: 'forbidden' | 'error'): void {
		if (session !== this.session) return;
		this.topics = [];
		this.fetchedAt = null;
		this.isLoading = false;
		this.failure = failure;
	}

	handleTopicLoaded(session: number, topic: FeedTopicResponse): void {
		if (session !== this.session) return;
		this.opened.delete(topic.id);
		this.opened.set(topic.id, topic);
		if (this.opened.size > OPENED_TOPICS_MAX) this.opened.delete(this.opened.keys().next().value!);
	}

	get(topicId: string): FeedTopicResponse | null {
		return this.topics.find((topic) => topic.id === topicId) ?? this.opened.get(topicId) ?? null;
	}
}

const feedTopics = new FeedTopics();
ResettableStates.register(feedTopics, feedTopics.reset);
AccountScopedWork.registerCancellation(() => feedTopics.reset());

export default feedTopics;
